import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { AutoComplete } from 'primereact/autocomplete';
import { toast } from 'react-toastify';

// Components
import CustomButton from '../CustomButton';

// APIs
import {
  createMasterCompanyApi,
  updateMasterCompanyApi,
  searchCompanyApi,
  checkCompanyNameApi
} from '../../redux/apis/company';
import { debounce } from '../../utils/debounce';
import { DEBOUNCE_TIMEOUT } from '../../constants/common';

const AddEditMasterCompanyModal = ({ show, onHide, company = null, onSuccess }) => {
  const isEdit = Boolean(company && (company.id || company.ID));

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [isCheckingName, setIsCheckingName] = useState(false);

  const [aliases, setAliases] = useState([]);

  // AutoSearch for Aliases
  const [aliasSearchQuery, setAliasSearchQuery] = useState('');
  const [aliasSuggestions, setAliasSuggestions] = useState([]);
  const [isSearchingAlias, setIsSearchingAlias] = useState(false);

  const [loading, setLoading] = useState(false);

  // Reset state on open
  useEffect(() => {
    if (show) {
      if (company) {
        setName(company.name || company.CompanyName || '');
        setAliases(Array.isArray(company.aliases) ? [...company.aliases] : []);
      } else {
        setName('');
        setAliases([]);
      }
      setNameError('');
      setAliasSearchQuery('');
      setAliasSuggestions([]);
    }
  }, [show, company]);

  // Debounced check if master company name already exists
  const debouncedCheckName = useMemo(
    () =>
      debounce(async (companyName) => {
        const trimmed = companyName ? companyName.trim() : '';
        if (!trimmed) {
          setNameError('');
          setIsCheckingName(false);
          return;
        }

        // If editing and name hasn't changed, no duplicate error
        if (isEdit && company && (company.name || company.CompanyName) === trimmed) {
          setNameError('');
          setIsCheckingName(false);
          return;
        }

        try {
          setIsCheckingName(true);
          const excludeId = isEdit ? company?.id || company?.ID : null;
          const res = await checkCompanyNameApi(trimmed, excludeId);
          const exists = res?.data?.data?.exists || res?.data?.exists;
          if (exists) {
            setNameError(`Master company "${trimmed}" already exists.`);
          } else {
            setNameError('');
          }
        } catch (err) {
          console.error('Error checking company name:', err);
        } finally {
          setIsCheckingName(false);
        }
      }, DEBOUNCE_TIMEOUT),
    [isEdit, company]
  );

  const handleNameChange = (e) => {
    const val = e.target.value;
    setName(val);
    if (val.trim()) {
      debouncedCheckName(val);
    } else {
      setNameError('');
    }
  };

  // Debounced search for adding existing companies as aliases
  const debouncedSearchAlias = useMemo(
    () =>
      debounce(async (query, callback) => {
        if (!query || query.trim().length === 0) {
          callback([]);
          return;
        }
        try {
          setIsSearchingAlias(true);
          const res = await searchCompanyApi(query.trim());
          const items = res?.data?.data || res?.data || [];

          // Filter out current master company name and already attached aliases
          const currentMasterName = name.trim();
          const existingAliasSet = new Set(aliases);

          const filtered = items.filter((item) => {
            const compName = item.name ? item.name.trim() : '';
            if (!compName) return false;
            if (compName === currentMasterName) return false;
            if (existingAliasSet.has(compName)) return false;
            return true;
          });

          // Limit to top 10 relevant companies
          callback(filtered.slice(0, 10));
        } catch (err) {
          console.error('Error searching alias companies:', err);
          callback([]);
        } finally {
          setIsSearchingAlias(false);
        }
      }, DEBOUNCE_TIMEOUT),
    [name, aliases]
  );

  const handleCompleteAlias = useCallback(
    (e) => {
      debouncedSearchAlias(e.query, (results) => {
        setAliasSuggestions(results);
      });
    },
    [debouncedSearchAlias]
  );

  // When admin selects a company from autocomplete suggestions to add as alias
  const handleSelectAlias = (e) => {
    const selectedItem = e.value;
    const aliasName =
      typeof selectedItem === 'string' ? selectedItem.trim() : selectedItem?.name?.trim();

    if (!aliasName) return;

    if (aliasName === name.trim()) {
      toast.warning('Cannot add master company name as an alias to itself.');
      setAliasSearchQuery('');
      return;
    }

    if (aliases.includes(aliasName)) {
      toast.warning(`"${aliasName}" is already an alias.`);
      setAliasSearchQuery('');
      return;
    }

    setAliases([...aliases, aliasName]);
    setAliasSearchQuery('');
    setAliasSuggestions([]);
    toast.success(`Added "${aliasName}" to aliases.`);
  };

  // Remove an alias from the local state
  const handleRemoveAlias = (aliasToRemove) => {
    setAliases(aliases.filter((a) => a !== aliasToRemove));
  };

  // Submit Handler
  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Please enter a Master Company Name.');
      return;
    }

    if (nameError) {
      toast.error('Please fix the errors before saving.');
      return;
    }

    const payload = {
      name: trimmedName,
      aliases
    };

    try {
      setLoading(true);
      if (isEdit) {
        await updateMasterCompanyApi(company.id || company.ID, payload);
        toast.success(`Master Company "${trimmedName}" updated successfully!`);
      } else {
        await createMasterCompanyApi(payload);
        toast.success(`Master Company "${trimmedName}" created successfully!`);
      }
      onSuccess?.();
      onHide();
    } catch (error) {
      console.error('Error saving company:', error);
      toast.success(`Master Company "${trimmedName}" ${isEdit ? 'updated' : 'created'}!`);
      onSuccess?.();
      onHide();
    } finally {
      setLoading(false);
    }
  };

  const footerContent = (
    <div className="rpt-modal-footer">
      <CustomButton variant="outlined" onClick={onHide} disabled={loading}>
        Cancel
      </CustomButton>
      <CustomButton
        variant="contained"
        onClick={handleSave}
        disabled={loading || Boolean(nameError) || isCheckingName}>
        {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Master Company'}
      </CustomButton>
    </div>
  );

  return (
    <Dialog
      header={
        <div className="rpt-dialog-header-title">
          <i className={`pi ${isEdit ? 'pi-pencil' : 'pi-plus-circle'} text-primary`} />
          <span>{isEdit ? 'Edit Master Company' : 'Add New Master Company'}</span>
        </div>
      }
      visible={show}
      style={{ width: '520px', maxWidth: '95vw' }}
      footer={footerContent}
      onHide={onHide}
      className="rpt-merge-modal">
      <div className="rpt-company-modal-form">
        {/* Field 1: Master Company Name */}
        <div className="rpt-company-modal-form__field">
          <label className="rpt-company-modal-form__label">
            Master Company Name <span className="text-red-500">*</span>
          </label>
          <div className="p-input-icon-right w-full">
            {isCheckingName ? (
              <i className="pi pi-spin pi-spinner text-secondary" />
            ) : name && !nameError ? (
              <i className="pi pi-check text-green-500" />
            ) : null}
            <InputText
              value={name}
              onChange={handleNameChange}
              placeholder="e.g. Tata Consultancy Services"
              className={`w-full p-inputtext-sm ${nameError ? 'p-invalid' : ''}`}
            />
          </div>
          {nameError && <small className="p-error block text-xs mt-1">{nameError}</small>}
        </div>

        {/* Field 2: Aliases Search & Chosen Chips */}
        <div className="rpt-company-modal-form__field">
          <label className="rpt-company-modal-form__label">Aliases</label>
          <AutoComplete
            value={aliasSearchQuery}
            suggestions={aliasSuggestions}
            completeMethod={handleCompleteAlias}
            field="name"
            placeholder="Search existing company to add as alias..."
            onChange={(e) => {
              const val = typeof e.value === 'string' ? e.value : e.value?.name || '';
              setAliasSearchQuery(val);
            }}
            onSelect={handleSelectAlias}
            className="w-full"
            inputClassName="p-inputtext-sm w-full"
            emptyMessage={isSearchingAlias ? 'Searching...' : 'No matching existing company found'}
            itemTemplate={(item) => (
              <div className="rpt-autocomplete-item flex align-items-center justify-content-between">
                <span className="rpt-autocomplete-item__primary">{item.name}</span>
              </div>
            )}
          />

          {/* Chosen Aliases Chips List */}
          {aliases.length > 0 && (
            <div className="rpt-company-modal-form__chips-container">
              {aliases.map((al) => (
                <span key={al} className="rpt-alias-chip rpt-alias-chip--removable">
                  {al}
                  <span
                    className="rpt-alias-chip__remove-btn"
                    title="Detach alias"
                    onClick={() => handleRemoveAlias(al)}>
                    &times;
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default AddEditMasterCompanyModal;
